// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * DSA 1.8 vector geometry in lon/lat (notes §4), shared by the browser
 * globe and the headset: segments are straight in lon/lat, so they are
 * subdivided to ≤ 1° before any projection; consecutive line and track
 * vertices join the short way round, crossing ±180 when |Δlon| > 180.
 *
 * Paths are returned "unwrapped" (longitudes continue past ±180) or split
 * at the antimeridian into parts, as each renderer needs.
 */
import type { LonLat } from './vectorModel'

/** Subdivision step, degrees (§4: ≤ 1°). */
export const SUBDIVIDE_DEG = 1

/** Longitudes made continuous: each vertex within 180° of the one before. */
export function unwrap(path: LonLat[]): LonLat[] {
  const out: LonLat[] = []
  let offset = 0
  for (let i = 0; i < path.length; i++) {
    const [lon, lat] = path[i]
    if (i > 0) {
      const prev = path[i - 1][0]
      const d = lon - prev
      if (d > 180) offset -= 360
      else if (d < -180) offset += 360
    }
    out.push([lon + offset, lat])
  }
  return out
}

/** Inserts vertices so no segment spans more than `step` degrees in lon or lat. */
export function subdivide(path: LonLat[], step = SUBDIVIDE_DEG): LonLat[] {
  if (path.length < 2) return path.slice()
  const out: LonLat[] = [path[0]]
  for (let i = 1; i < path.length; i++) {
    const [x0, y0] = path[i - 1]
    const [x1, y1] = path[i]
    const n = Math.max(1, Math.ceil(Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0)) / step))
    for (let k = 1; k <= n; k++) out.push([x0 + ((x1 - x0) * k) / n, y0 + ((y1 - y0) * k) / n])
  }
  return out
}

/** Back into −180..180. */
export function wrapLon(lon: number): number {
  return ((((lon + 180) % 360) + 360) % 360) - 180
}

/**
 * An unwrapped path cut where it crosses ±180 (and every 360° after), each
 * part in −180..180 with the cut vertex on the seam: what a renderer that
 * cannot draw past ±180 (MapLibre) needs.
 */
export function splitAtAntimeridian(unwrapped: LonLat[]): LonLat[][] {
  if (unwrapped.length === 0) return []
  const parts: LonLat[][] = []
  const worldOf = (lon: number) => Math.floor((lon + 180) / 360)
  let part: LonLat[] = []
  let world = worldOf(unwrapped[0][0])
  const push = (p: LonLat, w: number) => part.push([p[0] - w * 360, p[1]])
  push(unwrapped[0], world)
  for (let i = 1; i < unwrapped.length; i++) {
    const a = unwrapped[i - 1]
    const b = unwrapped[i]
    const wb = worldOf(b[0])
    while (wb !== world) {
      const dir = wb > world ? 1 : -1
      const seam = -180 + (world + (dir > 0 ? 1 : 0)) * 360
      const t = (seam - a[0]) / (b[0] - a[0])
      const lat = a[1] + t * (b[1] - a[1])
      part.push([dir > 0 ? 180 : -180, lat])
      parts.push(part)
      world += dir
      part = [[dir > 0 ? -180 : 180, lat]]
    }
    push(b, world)
  }
  parts.push(part)
  return parts.filter((p) => p.length >= 2)
}

/** Cumulative lengths along a path in lon/lat degrees (cos-lat scaled). */
function lengths(path: LonLat[]): number[] {
  const out = [0]
  for (let i = 1; i < path.length; i++) {
    const [x0, y0] = path[i - 1]
    const [x1, y1] = path[i]
    const c = Math.cos((((y0 + y1) / 2) * Math.PI) / 180)
    out.push(out[i - 1] + Math.hypot((x1 - x0) * c, y1 - y0))
  }
  return out
}

/** The first `fraction` (0..1) of a path by length: the `draw` effect. */
export function partialPath(path: LonLat[], fraction: number): LonLat[] {
  if (fraction >= 1 || path.length < 2) return path.slice()
  if (fraction <= 0) return []
  const L = lengths(path)
  const target = L[L.length - 1] * fraction
  const out: LonLat[] = [path[0]]
  for (let i = 1; i < path.length; i++) {
    if (L[i] >= target) {
      const t = L[i] === L[i - 1] ? 0 : (target - L[i - 1]) / (L[i] - L[i - 1])
      out.push([path[i - 1][0] + t * (path[i][0] - path[i - 1][0]), path[i - 1][1] + t * (path[i][1] - path[i - 1][1])])
      return out
    }
    out.push(path[i])
  }
  return out
}

export interface TrackCut {
  /** Vertices drawn, the head last (interpolated or the last fix passed). */
  path: LonLat[]
  /** Index of the source vertex each drawn vertex's segment starts from. */
  from: number[]
  head: LonLat | null
}

/**
 * A track drawn from its first vertex (or `T − window`) up to data time T
 * (§6, §7.6). With `interpolate`, the head moves between the fixes around
 * T; otherwise it sits on the last fix passed. Works on an unwrapped path.
 */
export function cutTrack(path: LonLat[], times: number[], T: number, interpolate: boolean, windowSeconds?: number): TrackCut {
  const n = Math.min(path.length, times.length)
  if (n === 0 || T < times[0]) return { path: [], from: [], head: null }
  const at = (t: number): { p: LonLat; i: number } => {
    let i = 0
    while (i < n - 1 && times[i + 1] <= t) i++
    if (i >= n - 1 || !interpolate) return { p: path[Math.min(i, n - 1)], i: Math.min(i, n - 1) }
    const span = times[i + 1] - times[i]
    const f = span > 0 ? (t - times[i]) / span : 0
    return { p: [path[i][0] + f * (path[i + 1][0] - path[i][0]), path[i][1] + f * (path[i + 1][1] - path[i][1])], i }
  }
  const end = at(Math.min(T, times[n - 1]))
  const startT = windowSeconds ? Math.max(times[0], T - windowSeconds) : times[0]
  const start = at(startT)
  const out: LonLat[] = [start.p]
  const from: number[] = [start.i]
  for (let i = start.i + 1; i <= end.i; i++) { out.push(path[i]); from.push(i) }
  const last = out[out.length - 1]
  if (end.p[0] !== last[0] || end.p[1] !== last[1]) { out.push(end.p); from.push(end.i) }
  return { path: out, from, head: end.p }
}
